import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Icon from '../components/Icon.jsx';
import { faqCategories, CONTACT } from '../mock';

const NAVY = '#1a3a5c';
const TEAL = '#3d94b8';

function Highlight({ text, term }) {
  if (!term) return text;
  const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'));
  return parts.map((p, i) => (p.toLowerCase() === term.toLowerCase() ? <mark key={i} className="rounded px-0.5" style={{ background: '#d7ecf4', color: NAVY }}>{p}</mark> : p));
}

export default function FAQ() {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const term = q.trim();

  const groups = useMemo(() => faqCategories
    .filter((c) => cat === 'all' || c.id === cat)
    .map((c) => ({ ...c, items: c.items.filter((f) => !term || `${f.q} ${f.a}`.toLowerCase().includes(term.toLowerCase())) }))
    .filter((c) => c.items.length > 0), [cat, term]);
  const total = groups.reduce((n, c) => n + c.items.length, 0);
  const allCount = faqCategories.reduce((n, c) => n + c.items.length, 0);

  return (
    <div>
      <section className="hero-bg py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <span className="font-semibold uppercase tracking-wider text-xs" style={{ color: TEAL }}>FAQ</span>
          <h1 className="mt-3 text-4xl md:text-5xl font-extrabold tracking-tight" style={{ color: NAVY }}>Häufige Fragen</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">{allCount} Antworten rund um Start, Unterricht, Tutoren, Preise und Organisation.</p>

          <div className="mt-8 max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="faq-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Frage suchen, z.B. Abo, Absage, Tutor ..."
              className="pl-11 pr-11 h-12 rounded-full bg-white border-slate-200 shadow-sm"
            />
            {q && (
              <button data-testid="faq-search-clear" onClick={() => setQ('')} aria-label="Suche löschen" className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            {[{ id: 'all', label: 'Alle Themen' }, ...faqCategories].map((c) => (
              <button
                key={c.id}
                data-testid={`faq-cat-${c.id}`}
                onClick={() => setCat(c.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all inline-flex items-center gap-1.5 ${cat === c.id ? 'text-white shadow-md' : 'bg-white text-slate-700 border border-slate-200 hover:border-teal-300'}`}
                style={cat === c.id ? { background: TEAL } : {}}
              >
                {c.icon && <Icon name={c.icon} className="h-4 w-4" />} {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <p data-testid="faq-count" className="text-sm text-slate-500 mb-6">{total} {total === 1 ? 'Frage' : 'Fragen'}{term ? ` zu „${term}“` : ''}</p>

          {groups.length === 0 ? (
            <div data-testid="faq-empty" className="text-center py-16 card-soft">
              <p className="font-semibold" style={{ color: NAVY }}>Dazu haben wir noch keine Antwort.</p>
              <p className="mt-2 text-sm text-slate-600">Frag uns direkt – wir antworten schnell.</p>
              <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="inline-block mt-5"><Button className="btn-whatsapp rounded-full h-11 px-6">Per WhatsApp fragen</Button></a>
            </div>
          ) : (
            <div className="space-y-12">
              {groups.map((c) => (
                <div key={c.id} data-testid={`faq-group-${c.id}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: '#e6f2f7' }}>
                      <Icon name={c.icon} className="h-5 w-5" style={{ color: TEAL }} />
                    </div>
                    <h2 className="text-xl md:text-2xl font-extrabold" style={{ color: NAVY }}>{c.label}</h2>
                    <span className="ml-auto text-xs font-semibold text-slate-500">{c.items.length}</span>
                  </div>
                  <Accordion
                    key={term ? 'search' : 'browse'}
                    type={term ? 'multiple' : 'single'}
                    {...(term ? { value: c.items.map((_, i) => `${c.id}-${i}`) } : { collapsible: true })}
                    className="space-y-3"
                  >
                    {c.items.map((f, i) => (
                      <AccordionItem key={f.q} value={`${c.id}-${i}`} className="card-soft border-0 px-6 py-1" data-testid="faq-item">
                        <AccordionTrigger className="text-left font-semibold hover:no-underline py-5 [&>svg]:shrink-0" style={{ color: NAVY }}>
                          <span><Highlight text={f.q} term={term} /></span>
                        </AccordionTrigger>
                        <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                          <Highlight text={f.a} term={term} />
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f5f9fb 100%)' }}>
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <h3 className="text-2xl md:text-3xl font-extrabold" style={{ color: NAVY }}>Noch Fragen?</h3>
          <p className="mt-3 text-slate-600">Schreib uns direkt – wir melden uns innerhalb von 24 Stunden.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/kontakt"><Button className="btn-teal rounded-full h-12 px-6">Kontakt aufnehmen</Button></Link>
            <Link to="/buchen"><Button variant="outline" className="btn-outline-teal rounded-full h-12 px-6">Kostenlose Bedarfsanalyse</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
